import { clearState } from '@/app/store/clearSlice';
import { createSlice } from '@reduxjs/toolkit';
import { getColorCodeInitials, isExternalEmail } from '../utils/userHelpers';

const initialState = {
   users: {},
};

const userInfoSlice = createSlice({
   name: 'userInfoData',
   initialState,
   reducers: {
      setUserInfoMap(state, action) {
         const usersMap = action.payload;

         usersMap.forEach(userData => {
            const userId = userData.userId;
            const existingUser = state.users[userId] || {};
            const profile = userData.userProfile || {};

            const image = userData.image ?? userData.profileImage ?? profile.image ?? existingUser.image ?? '';
            const nickName =
               userData.nickName ?? userData.name ?? profile.nickName ?? profile.name ?? existingUser.nickName ?? '';
            const email = userData.email ?? profile.email ?? existingUser.email ?? '';

            state.users[userId] = {
               ...state.users[userId],
               userId,
               status: userData.status ?? profile.status ?? existingUser.status ?? '',
               mobileNumber: userData.mobileNumber ?? profile.mobileNumber ?? existingUser.mobileNumber ?? '',
               isAdminBlocked: userData.isAdminBlocked ?? profile.isAdminBlocked ?? existingUser.isAdminBlocked ?? 0,
               thumbImage: userData.thumbImage ?? profile.thumbImage ?? existingUser.thumbImage ?? '',
               image,
               nickName,
               email,
               // Backend flag is the primary signal; the email-domain check is an optional
               // extra net so a user still gets tagged External if the backend hasn't told
               // us yet but their email domain clearly isn't the org's own.
               isExternal:
                  (userData.isExternal ?? profile.isExternal ?? existingUser.isExternal ?? false) ||
                  (email ? isExternalEmail(email) : false),
               colorCode: userData.colorCode ?? existingUser.colorCode ?? getColorCodeInitials(nickName),
               isDeleted: false,
            };
         });
      },
      resetUserInfoData(state) {
         state.users = initialState.users;
      },
      updateBlockUser(state, action) {
         const { userId, isBlocked } = action.payload;
         if (state.users[userId]) {
            state.users[userId].isBlockedByMe = isBlocked;
         }
      },
      updateIsBlockedMe(state, action) {
         const { userId, isBlockedMe } = action.payload;
         if (state.users[userId]) {
            state.users[userId].isBlockedMe = isBlockedMe;
         }
      },
      updateUserInfo(state, action) {
         const { userId, userData } = action.payload;
         if (state.users[userId]) {
            state.users[userId] = {
               ...state.users[userId],
               ...userData,
            };
         }
      },
      updateUserDeleted(state, action) {
         const { userId, isDeleted, deletedUserName } = action.payload;

         if (state.users[userId]) {
            state.users[userId].isDeleted = isDeleted;
            state.users[userId].nickName = deletedUserName;
         } else {
            state.users[userId] = {
               userId,
               isDeleted,
               colorCode: getColorCodeInitials(userId),
               nickName: deletedUserName,
            };
         }
      },
      setBlockedUsersList(state, action) {
         const blockedJids = action.payload;
         Object.values(state.users).forEach(user => {
            user.isBlockedByMe = 0;
         });
         blockedJids.forEach(id => {
            if (!state.users[id]) {
               state.users[id] = { userId: id, colorCode: getColorCodeInitials(id) };
            }
            state.users[id].isBlockedByMe = 1;
         });
      },
      setMutedUsersList(state, action) {
         const mutedChats = action.payload;
         Object.values(state.users).forEach(user => {
            user.isMuted = false;
         });
         mutedChats.forEach(chat => {
            const id = chat.chatId || chat.userId; // Handle robustly
            if (id) {
               if (!state.users[id]) {
                  state.users[id] = { userId: id, colorCode: getColorCodeInitials(id) };
               }
               state.users[id].isMuted = true;
            }
         });
      },
      setBlockedMeUsersList(state, action) {
         const blockedMeJids = action.payload;
         Object.values(state.users).forEach(user => {
            user.isBlockedMe = 0;
         });
         blockedMeJids.forEach(id => {
            if (!state.users[id]) {
               state.users[id] = { userId: id, colorCode: getColorCodeInitials(id) };
            }
            state.users[id].isBlockedMe = 1;
         });
      },
   },
   extraReducers: builder => {
      builder.addCase(clearState, () => initialState);
   },
});

export const {
   resetUserInfoData,
   setUserInfoMap,
   updateBlockUser,
   updateIsBlockedMe,
   updateUserInfo,
   setBlockedUsersList,
   setBlockedMeUsersList,
   setMutedUsersList,
   updateUserDeleted,
} = userInfoSlice.actions;
export const { reducer: userInfoReducer } = userInfoSlice;
